import { useEffect } from 'react';
import { useActualizacionStore } from '../lib/actualizacion.js';
import { megas } from '../lib/versionApp.js';
import { useHubStore } from '../lib/hub.js';
import { useAtrasCierra } from '../lib/atras.js';
import { IcoClose } from './common/Icons.jsx';
import hoja from './MoveTabSheet.module.css';
import styles from './AvisoActualizacion.module.css';

const CADA_MS = 6 * 60 * 60 * 1000;

/**
 * "Hay una versión nueva de la app": la hoja con el botón para actualizar.
 *
 * Pregunta al servidor al abrir la app, al volver a primer plano y cada seis
 * horas (la tablet de la caja no se cierra en días). Solo hace algo en la app
 * de Android; en el navegador y en iOS no se pinta nada.
 */
export default function AvisoActualizacion() {
  const ultima      = useActualizacionStore((s) => s.ultima);
  const instalada   = useActualizacionStore((s) => s.instalada);
  const obligatoria = useActualizacionStore((s) => s.obligatoria);
  const abierta     = useActualizacionStore((s) => s.abierta);
  const estado      = useActualizacionStore((s) => s.estado);
  const progreso    = useActualizacionStore((s) => s.progreso);
  const error       = useActualizacionStore((s) => s.error);
  const { revisar, actualizar, posponer, ocultar } = useActualizacionStore.getState();
  const esCaja = useHubStore((s) => s.embedded);

  useEffect(() => {
    // Unos segundos después de abrir: primero que la app termine de arrancar
    const inicio = setTimeout(() => revisar(), 8000);
    const alVolver = () => { if (document.visibilityState === 'visible') revisar(); };
    document.addEventListener('visibilitychange', alVolver);
    const cada = setInterval(() => revisar(), CADA_MS);
    return () => {
      clearTimeout(inicio);
      clearInterval(cada);
      document.removeEventListener('visibilitychange', alVolver);
    };
  }, [revisar]);

  const descargando = estado === 'descargando';
  // Atrás: mientras descarga solo esconde la hoja; si no, es "Después"
  useAtrasCierra(abierta && !obligatoria, descargando ? ocultar : posponer);

  if (!abierta || !ultima) return null;

  const pct = Math.round(progreso * 100);

  return (
    <div className={hoja.scrim} role="presentation">
      <div className={hoja.sheet} role="alertdialog" aria-modal="true" aria-labelledby="actualizacion-titulo">
        <div className={hoja.head}>
          <div>
            <h2 className={hoja.title} id="actualizacion-titulo">Hay una versión nueva de la app</h2>
            <p className={hoja.sub}>
              Versión <span className={styles.version}>{ultima.versionName}</span> · {megas(ultima.tamano)}
            </p>
            {instalada?.versionName && instalada.versionName !== '1.0' && (
              <p className={hoja.sub}>Tienes la <span className={styles.version}>{instalada.versionName}</span></p>
            )}
          </div>
          {!obligatoria && (
            <button
              className={hoja.close}
              onClick={descargando ? ocultar : posponer}
              aria-label={descargando ? 'Seguir trabajando' : 'Después'}
            >
              <IcoClose size={16} />
            </button>
          )}
        </div>

        {ultima.notas && <p className={styles.notas}>{ultima.notas}</p>}

        {obligatoria && (
          <p className={styles.importante}>
            Esta actualización es necesaria: la versión que tienes ya no funciona bien con el servidor.
          </p>
        )}

        {estado === 'nada' && (
          <>
            <p className={styles.texto}>
              Se descarga aquí mismo. Después Android te pide confirmar con «Actualizar»; la app se cierra un
              momento y, al terminar, tocas «Abrir». Tus datos se quedan.
            </p>
            {esCaja && (
              <p className={styles.importante}>
                Esta tablet es la caja principal: mientras se instala, las comanderas pierden la conexión uno o
                dos minutos. Mejor hazlo cuando no haya cuentas abiertas.
              </p>
            )}
          </>
        )}

        {descargando && (
          <div className={styles.progreso} aria-live="polite">
            <div className={styles.barra} role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct}>
              <span style={{ width: `${pct}%` }} />
            </div>
            <p className={styles.texto}>
              Descargando… {pct}%. Puedes seguir trabajando: al terminar se abre el instalador.
            </p>
          </div>
        )}

        {estado === 'instalando' && (
          <p className={styles.texto}>
            Toca «Actualizar» en la ventana de Android y, al terminar, «Abrir». Si la cerraste sin querer,
            vuelve a tocar Instalar.
          </p>
        )}

        {estado === 'permiso' && (
          <p className={styles.importante}>
            Falta un permiso, solo esta vez: en la pantalla que se abrió activa «Permitir de esta fuente»,
            regresa y toca Instalar.
          </p>
        )}

        {estado === 'error' && <p className={hoja.error}>{error}</p>}

        <div className={hoja.acciones}>
          {descargando ? (
            <button className="btn btn--ghost" onClick={ocultar}>Seguir trabajando</button>
          ) : (
            <>
              {!obligatoria && <button className="btn btn--ghost" onClick={posponer}>Después</button>}
              <button className="btn btn--primary" onClick={actualizar}>
                {estado === 'nada' ? 'Actualizar' : estado === 'error' ? 'Reintentar' : 'Instalar'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
